import {
  LitElement,
  html,
  unsafeHTML,
  css,
  ifDefined,
  classMap,
} from '@hh/global.js'

class HHCustomSelect extends LitElement {
  static properties = {
    options: {
      type: Object,
    },
    name: {
      type: String,
    },
    active: {
      type: String,
    },
    lang: {
      type: Boolean,
    },
    position: {
      type: String,
    },
    _selected: {
      state: true,
    },
    _active: {
      state: true,
    },
    radios: {
      type: Boolean,
    },
    disabled: {
      type: Boolean,
    },
  }

  constructor() {
    super()
    this.handleBodyClick = this.handleBodyClick.bind(this)
    this.uuid = Math.random() * (1000000 - 1) + 1
    this.position = 'bottom'
  }

  connectedCallback() {
    super.connectedCallback()
    this._selected = this.active
    document.body.addEventListener('click', this.handleBodyClick)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    document.body.addEventListener('click', this.handleBodyClick)
  }

  handleBodyClick(e) {
    const path = e.composedPath()
    if (
      path.some((node) => {
        return node === this
      })
    ) {
      return true
    }

    this._active && this.toggle()
  }

  toggle(e = false) {
    if (e && e.type === 'keyup' && !~['ESCAPE'].indexOf(e.code.toUpperCase())) {
      return true
    }

    this._active = !this._active
  }

  selectValue(e, value) {
    e.preventDefault()
    this._selected = value
    ;(this.querySelector('[slot="input"]') || {}).value = value
    this.dispatchEvent(
      new CustomEvent('change', {
        bubbles: true,
        composed: true,
        detail: {
          value,
        },
      })
    )
    this.toggle()
  }

  get selectedLabel() {
    const option = this.options.find(
      (option) => option.value === this._selected
    )
    if (!option) return 'Invalid selected option'
    return option.label
  }

  sanitize(value) {
    return value.replace('&amp;', '&')
  }

  render() {
    return html`<div>
      <div class="container ${this.disabled && 'disabled'}">
        <div id="${this.uuid}-label" @click=${this.toggle}>
          <slot name="title"></slot>
        </div>
        <button
          @click=${this.toggle}
          type="button"
          class="button"
          aria-expanded="${this._active}"
          aria-controls="${this.uuid}-list"
          aria-describedby="${this.uuid}-label"
        >
          <span class="button__label">${unsafeHTML(this.selectedLabel)}</span>
          <slot name="icon"></slot>
        </button>
        <slot name="input"></slot>
        <hh-focus-trap active="${this._active}">
          <ul
            id="${this.uuid}-list"
            class="${classMap({
              list: true,
              'is-active': this._active,
              'position-top': this.position === 'top',
            })}"
            @keyup="${this.toggle}"
            aria-hidden="${!this._active}"
            role="list"
          >
            ${this.options.map((option) => {
              this.lang && (option.lang = option.value)
              return html`<li
                class="${classMap({
                  option: true,
                  'is-active': option.value === this._selected,
                })}"
              >
                <a
                  @click="${(e) => this.selectValue(e, option.value)}"
                  class="link"
                  href="#"
                  hreflang="${ifDefined(option.lang)}"
                  lang="${ifDefined(option.lang)}"
                >
                  ${this.radios && html` <span class="checkmark"></span> `}
                  <span>${unsafeHTML(this.sanitize(option.label))}</span>
                </a>
              </li>`
            })}
          </ul>
        </hh-focus-trap>
      </div>
    </div>`
  }

  static styles = css`
    :host {
      width: 100%;
      display: block;
    }

    :host(.error) {
      border-bottom: 0.1rem solid var(--hh-color-danger);
    }

    .container {
      position: relative;
    }

    .disabled {
      pointer-events: none;
    }

    .button {
      align-items: center;
      background-color: var(--hh-color-light);
      border: 0;
      cursor: pointer;
      display: flex;
      font: var(--hh-body-font);
      height: var(--hh-height, 50px);
      justify-content: space-between;
      padding: 0 10px;
      width: 100%;
      color: var(--hh-color-ink);
    }

    @media screen and (min-width: 1920px) {
      .button {
        height: max(calc(50 / var(--screen-size) * 100vw), 50px);
      }
    }

    .button__label {
      position: relative;
      top: 8px;
      color: var(--hh-color-night);
      text-align: left;
    }

    @media screen and (min-width: 1920px) {
      .button__label {
        top: max(calc(8 / var(--screen-size) * 100vw), 8px);
      }
    }

    ::slotted([slot='button']),
    ::slotted([slot='icon']) {
      align-items: center;
      display: flex;
      height: 100%;
    }

    ::slotted([slot='icon']) {
      margin-left: 30px;
      width: 10px;
    }

    @media screen and (min-width: 1920px) {
      ::slotted([slot='icon']) {
        margin-left: max(calc(30 / var(--screen-size) * 100vw), 30px);
        width: max(calc(10 / var(--screen-size) * 100vw), 10px);
      }
    }

    .button[aria-expanded='true'] ::slotted([slot='icon']) {
      transform: rotate(180deg);
    }

    ::slotted([slot='title']) {
      color: var(--hh-color-subdued);
      left: 10px;
      margin: 0;
      position: absolute;
      top: 8px;
    }

    @media screen and (min-width: 1920px) {
      ::slotted([slot='title']) {
        top: max(calc(8 / var(--screen-size) * 100vw), 8px);
      }
    }

    .container:hover ::slotted([slot='title']) {
      color: var(--hh-color-night);
    }

    .list {
      background-color: var(--hh-background, #f6f4f0);
      border: var(--hh-border-width, 1px) solid var(--hh-color-neutral);
      display: block;
      margin: 0;
      max-height: var(--hh-options-max-height, 19rem);
      min-height: var(--hh-options-min-height, 8.2rem);
      opacity: 0;
      overflow-y: auto;
      padding-left: 0;
      position: absolute;
      transition: visibility var(--hh-duration-default, 300ms)
          var(--hh-duration-default, 300ms),
        opacity var(--hh-duration-default, 300ms);
      visibility: hidden;
      width: 100%;
      z-index: var(--hh-z-index, 10);
      box-sizing: border-box;
      pointer-events: none;
    }

    .list.position-top {
      bottom: 100%;
    }

    .list.is-active {
      pointer-events: fill;
      opacity: 1;
      transition: opacity var(--hh-duration-default, 300ms) 1ms, visibility 1ms;
      visibility: visible;
    }

    .option {
      list-style: none;
      padding: 2px;
      border-bottom: 0.1rem solid var(--hh-color-subdued);
    }

    .option:last-of-type {
      border-bottom: 0;
    }

    .link {
      color: var(--hh-color-subdued);
      display: block;
      padding: 8px;
      text-decoration: none;
      font: var(--hh-body-caption);
      letter-spacing: var(--hh-caption-letter-spacing);
      text-transform: var(--hh-caption-text-transform);
      display: flex;
      align-items: center;
    }

    @media screen and (min-width: 1920px) {
      .link {
        padding: max(calc(8 / var(--screen-size) * 100vw), 8px);
      }
    }

    .checkmark {
      height: 1.2rem;
      width: 1.2rem;
      border-radius: 50%;
      box-sizing: border-box;
      border: 0.1rem solid var(--hh-color-subdued);
      margin-right: 1rem;
    }

    @media screen and (min-width: 1920px) {
      .checkmark {
        height: max(calc(12 / var(--screen-size) * 100vw), 1.2rem);
        width: max(calc(12 / var(--screen-size) * 100vw), 1.2rem);
        margin-right: max(calc(10 / var(--screen-size) * 100vw), 1rem);
      }
    }

    .option.is-active .checkmark {
      background-color: var(--hh-color-night);
      border-color: var(--hh-color-night);
    }

    .link:hover .checkmark {
      border-color: var(--hh-color-night);
    }

    .option.is-active span,
    .link:hover span {
      color: var(--hh-color-night);
    }

    .disabled .button__label,
    .disabled ::slotted([slot='title']),
    .disabled ::slotted([slot='icon']) {
      color: var(--hh-color-neutral);
    }

    :host([theme='tertiary']) .container {
      display: flex;
      position: relative;
    }

    @media screen and (max-width: 749px) {
      :host([theme='tertiary']) .container:after {
        content: '';
        position: absolute;
        left: 0;
        bottom: -0.15rem;
        background-color: var(--hh-text-color, #000);
        width: 100%;
        height: var(--hh-underline-width, 0.1rem);
        pointer-events: none;
      }
    }

    :host([theme='tertiary']) button {
      padding: 0;
      height: auto;
      background: transparent;
    }

    :host([theme='tertiary']) ::slotted([slot='title']) {
      position: unset;
      display: flex;
      color: var(--hh-text-color);
      padding-right: 0;
      white-space: nowrap;
      cursor: pointer;
    }

    @media screen and (min-width: 990px) {
      :host([theme='tertiary']) ::slotted([slot='title']) {
        padding-right: 0.2rem;
      }
    }

    :host([theme='tertiary']) ::slotted([slot='icon']) {
      margin-left: 0.5rem;
      color: var(--hh-text-color);
    }

    :host([theme='tertiary']) .button__label {
      top: 0;
      color: var(--hh-text-color);
      font: var(--hh-body-caption);
      letter-spacing: var(--hh-caption-letter-spacing);
      text-transform: var(--hh-caption-text-transform);
      white-space: nowrap;
      text-transform: var(--hh-body-text-transform, unset);
    }

    @media screen and (max-width: 749px) {
      :host([theme='tertiary']) .button__label {
        display: none;
      }
    }

    :host([theme='tertiary']) .list {
      right: 0;
      top: calc(100% + 2rem);
      width: var(--hh-width-list, 16rem);
      border: 0;
    }

    @media screen and (min-width: 990px) {
      :host([theme='tertiary']) .list {
        top: calc(100% + 2rem);
      }
    }

    @media screen and (min-width: 990px) {
      :host([theme='tertiary']) .list {
        top: calc(100% + 3rem);
      }
    }

    :host([theme='tertiary']) .option {
      border-bottom: 0;
      padding: 0 2rem;
      display: flex;
      align-items: center;
      height: 4rem;
    }

    :host([theme='tertiary']) .link {
      padding: 0;
      display: block;
      width: 100%;
      height: 100%;
      text-transform: var(--hh-body-text-transform, unset);
    }

    :host([theme='tertiary']) .link > span {
      display: flex;
      align-items: center;
      width: 100%;
      justify-content: space-between;
      height: 100%;
    }

    :host([type='variant']) .container {
      display: inline-flex;
    }

    :host([type='variant']) ::slotted([slot='title']) {
      display: none;
    }

    :host([type='variant']) button {
      height: var(--hh-height);
    }

    :host([type='variant']) .button__label {
      font: var(--hh-body-font);
      letter-spacing: var(--hh-body-letter-spacing);
      text-transform: var(--hh-body-text-transform);
      display: flex;
      justify-content: space-between;
      width: var(--hh-width, auto);
    }

    :host([type='variant']) .list,
    :host([type='sizing_chart']) .list {
      right: unset;
      left: 0;
      top: calc(100% + var(--hh-spacing-md) + 0.1rem);
      box-shadow: 0.4rem 0.4rem 3.7rem 0 rgba(0, 0, 0, 0.05);
    }

    @media screen and (min-width: 990px) {
      :host([type='variant']) .list,
      :host([type='sizing_chart']) .list {
        top: calc(100% + var(--hh-spacing-sm) + 0.1rem);
      }
    }

    :host([type='sizing_chart']) .button__label {
      font: var(--hh-h2-font);
      letter-spacing: var(--hh-h2-letter-spacing);
      text-transform: var(--hh-h2-text-transform);
      font-style: italic;
    }

    :host([type='sizing_chart']) .link {
      font: var(--hh-h4-font);
      letter-spacing: var(--hh-h4-letter-spacing);
      text-transform: var(--hh-h4-text-transform);
    }

    @media screen and (max-width: 749px) {
      :host([type='sizing_chart']) .container:after {
        display: none;
      }

      :host([type='variant']) .button__label,
      :host([type='sizing_chart']) .button__label {
        display: flex;
      }
    }
  `
}

customElements.define('hh-custom-select', HHCustomSelect)
